// VIBRANIAM-SPACE v2 - nodes/signal.js
// Control-rate nodes. Every eval() is a pure function of (inputs, params,
// context, state) - no clocks, no Math.random. State is explicit so a node can
// be reset and so the whole graph stays reproducible from frame 0.

import { EMPTY_ARRAY } from '../types.js';

const ACC = {
  audio: '#8ef07a',
  shape: '#4fd8ff',
  gen:   '#ffb454',
  math:  '#9c7cf0',
  arr:   '#e05a9e',
};

const num = (v, d = 0) => (Number.isFinite(v) ? v : d);
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** One-pole coefficient for a time constant in milliseconds. */
const poleCoeff = (ms, dt) => (ms <= 0 ? 1 : 1 - Math.exp(-dt / (ms / 1000)));

export const SIGNAL_NODES = {
  // ---------------------------------------------------------------- audio
  'a.bands': {
    type: 'a.bands', title: 'Bande FFT', group: 'Audio', accent: ACC.audio,
    hint: 'Tutte le bande logaritmiche come array.',
    inputs: [], outputs: [{ id: 'out', kind: 'array', label: 'bande' }],
    params: {}, schema: [],
    eval: (_i, _p, ctx) => ({ out: ctx.features.bands || EMPTY_ARRAY }),
  },

  'a.band': {
    type: 'a.band', title: 'Banda', group: 'Audio', accent: ACC.audio,
    hint: 'Energia di una singola banda.',
    inputs: [], outputs: [{ id: 'out', kind: 'scalar', label: 'valore' }],
    params: { index: 3, gain: 12 },
    schema: [
      { key: 'index', label: 'Indice', min: 0, max: 127, step: 1, mod: true },
      { key: 'gain',  label: 'Guadagno', min: 0, max: 60, step: 0.5, mod: true },
    ],
    eval: (_i, p, ctx) => {
      const b = ctx.features.bands;
      if (!b || !b.length) return { out: 0 };
      const i = Math.max(0, Math.min(b.length - 1, Math.round(num(p.index))));
      return { out: clamp01(b[i] * num(p.gain, 12)) };
    },
  },

  'a.energy': {
    type: 'a.energy', title: 'Energia', group: 'Audio', accent: ACC.audio,
    hint: 'RMS o una delle tre fasce.',
    inputs: [], outputs: [{ id: 'out', kind: 'scalar', label: 'valore' }],
    params: { range: 'rms', gain: 4 },
    schema: [
      { key: 'range', label: 'Fascia', type: 'select', options: ['rms', 'bass', 'mid', 'high'] },
      { key: 'gain',  label: 'Guadagno', min: 0, max: 60, step: 0.5, mod: true },
    ],
    eval: (_i, p, ctx) => {
      const f = ctx.features;
      const raw = p.range === 'bass' ? f.bass : p.range === 'mid' ? f.mid
                : p.range === 'high' ? f.high : f.rms;
      return { out: clamp01(num(raw) * num(p.gain, 4)) };
    },
  },

  'a.onset': {
    type: 'a.onset', title: 'Onset', group: 'Audio', accent: ACC.audio,
    hint: 'Impulso sui transienti rilevati dallo spectral flux.',
    inputs: [], outputs: [{ id: 'out', kind: 'trigger', label: 'trig' }],
    params: {}, schema: [],
    eval: (_i, _p, ctx) => ({ out: !!ctx.features.onset }),
  },

  'a.flux': {
    type: 'a.flux', title: 'Flux', group: 'Audio', accent: ACC.audio,
    hint: 'Spectral flux grezzo: quanto lo spettro sta cambiando.',
    inputs: [], outputs: [{ id: 'out', kind: 'scalar', label: 'valore' }],
    params: { gain: 60 },
    schema: [{ key: 'gain', label: 'Guadagno', min: 1, max: 400, step: 1, mod: true }],
    eval: (_i, p, ctx) => ({ out: clamp01(num(ctx.features.flux) * num(p.gain, 60)) }),
  },

  'a.centroid': {
    type: 'a.centroid', title: 'Centroide', group: 'Audio', accent: ACC.audio,
    hint: 'Baricentro spettrale normalizzato: chiaro contro scuro.',
    inputs: [], outputs: [{ id: 'out', kind: 'scalar', label: 'valore' }],
    params: { ceiling: 6000 },
    schema: [{ key: 'ceiling', label: 'Tetto Hz', min: 500, max: 16000, step: 100, mod: true }],
    eval: (_i, p, ctx) => ({ out: clamp01(num(ctx.features.centroid) / num(p.ceiling, 6000)) }),
  },

  // ---------------------------------------------------------------- shapers
  's.curve': {
    type: 's.curve', title: 'Curva', group: 'Forma', accent: ACC.shape,
    hint: 'Piega la risposta: esponenziale, logaritmica o a S.',
    inputs: [{ id: 'in', kind: 'scalar', label: 'in' }],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { shape: 'pow', exponent: 2 },
    schema: [
      { key: 'shape', label: 'Forma', type: 'select', options: ['pow', 'root', 'scurve', 'invert'] },
      { key: 'exponent', label: 'Esponente', min: 0.1, max: 6, step: 0.05, mod: true },
    ],
    eval: (i, p) => {
      const v = clamp01(num(i.in));
      const e = num(p.exponent, 2);
      if (p.shape === 'root')   return { out: Math.pow(v, 1 / e) };
      if (p.shape === 'scurve') return { out: v * v * (3 - 2 * v) };
      if (p.shape === 'invert') return { out: 1 - v };
      return { out: Math.pow(v, e) };
    },
  },

  's.remap': {
    type: 's.remap', title: 'Remap', group: 'Forma', accent: ACC.shape,
    hint: 'Porta un intervallo dentro un altro.',
    inputs: [{ id: 'in', kind: 'scalar', label: 'in' }],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { inMin: 0, inMax: 1, outMin: 0, outMax: 1, clamp: true },
    schema: [
      { key: 'inMin',  label: 'In min',  min: -10, max: 10, step: 0.01, mod: true },
      { key: 'inMax',  label: 'In max',  min: -10, max: 10, step: 0.01, mod: true },
      { key: 'outMin', label: 'Out min', min: -10, max: 10, step: 0.01, mod: true },
      { key: 'outMax', label: 'Out max', min: -10, max: 10, step: 0.01, mod: true },
      { key: 'clamp',  label: 'Limita',  type: 'bool' },
    ],
    eval: (i, p) => {
      const a = num(p.inMin), b = num(p.inMax, 1);
      const span = b - a;
      let t = span === 0 ? 0 : (num(i.in) - a) / span;
      if (p.clamp) t = clamp01(t);
      return { out: num(p.outMin) + (num(p.outMax, 1) - num(p.outMin)) * t };
    },
  },

  's.smooth': {
    type: 's.smooth', title: 'Smooth', group: 'Forma', accent: ACC.shape,
    hint: 'Attacco e rilascio indipendenti: il modo giusto per addolcire senza perdere i colpi.',
    inputs: [{ id: 'in', kind: 'scalar', label: 'in' }],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { attack: 8, release: 220 },
    schema: [
      { key: 'attack',  label: 'Attacco ms',  min: 0, max: 1000, step: 1, mod: true },
      { key: 'release', label: 'Rilascio ms', min: 0, max: 4000, step: 1, mod: true },
    ],
    state: () => ({ v: 0 }),
    eval: (i, p, ctx, st) => {
      const target = num(i.in);
      const k = poleCoeff(target > st.v ? num(p.attack, 8) : num(p.release, 220), ctx.dt);
      st.v += (target - st.v) * k;
      return { out: st.v };
    },
  },

  's.gate': {
    type: 's.gate', title: 'Soglia', group: 'Forma', accent: ACC.shape,
    hint: 'Apre sopra la soglia, chiude sotto soglia meno isteresi.',
    inputs: [{ id: 'in', kind: 'scalar', label: 'in' }],
    outputs: [
      { id: 'out',  kind: 'scalar',  label: 'aperto' },
      { id: 'trig', kind: 'trigger', label: 'fronte' },
    ],
    params: { threshold: 0.5, hysteresis: 0.08 },
    schema: [
      { key: 'threshold',  label: 'Soglia',  min: 0, max: 1, step: 0.005, mod: true },
      { key: 'hysteresis', label: 'Isteresi', min: 0, max: 0.5, step: 0.005, mod: true },
    ],
    state: () => ({ open: false }),
    eval: (i, p, _ctx, st) => {
      const v = num(i.in);
      const th = num(p.threshold, 0.5);
      const hy = num(p.hysteresis, 0.08);
      const was = st.open;
      if (!st.open && v >= th) st.open = true;
      else if (st.open && v < th - hy) st.open = false;
      return { out: st.open ? 1 : 0, trig: st.open && !was };
    },
  },

  's.hold': {
    type: 's.hold', title: 'Sample & Hold', group: 'Forma', accent: ACC.shape,
    hint: 'Congela il valore in ingresso a ogni impulso.',
    inputs: [
      { id: 'in',   kind: 'scalar',  label: 'in' },
      { id: 'trig', kind: 'trigger', label: 'trig' },
    ],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: {}, schema: [],
    state: () => ({ v: 0 }),
    eval: (i, _p, _ctx, st) => {
      if (i.trig) st.v = num(i.in);
      return { out: st.v };
    },
  },

  's.quantize': {
    type: 's.quantize', title: 'Quantizza', group: 'Forma', accent: ACC.shape,
    hint: 'Riduce a gradini: movimenti a scatti invece che continui.',
    inputs: [{ id: 'in', kind: 'scalar', label: 'in' }],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { steps: 8 },
    schema: [{ key: 'steps', label: 'Gradini', min: 2, max: 64, step: 1, mod: true }],
    eval: (i, p) => {
      const n = Math.max(2, Math.round(num(p.steps, 8)));
      return { out: Math.round(clamp01(num(i.in)) * (n - 1)) / (n - 1) };
    },
  },

  // ------------------------------------------------------------- generators
  'g.lfo': {
    type: 'g.lfo', title: 'LFO', group: 'Generatori', accent: ACC.gen,
    hint: 'Oscillatore lento. La fase avanza col tempo del frame, quindi e riproducibile.',
    inputs: [], outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { rate: 0.25, shape: 'sine', phase: 0, bipolar: false },
    schema: [
      { key: 'rate',  label: 'Hz', min: 0, max: 20, step: 0.005, mod: true },
      { key: 'shape', label: 'Forma', type: 'select', options: ['sine', 'tri', 'saw', 'square', 'noise'] },
      { key: 'phase', label: 'Fase', min: 0, max: 1, step: 0.005, mod: true },
      { key: 'bipolar', label: 'Bipolare', type: 'bool' },
    ],
    state: () => ({ phase: 0 }),
    eval: (_i, p, ctx, st) => {
      st.phase += num(p.rate, 0.25) * ctx.dt;
      st.phase -= Math.floor(st.phase);
      const t = (st.phase + num(p.phase)) % 1;
      let v;
      switch (p.shape) {
        case 'tri':    v = 1 - Math.abs(t * 2 - 1); break;
        case 'saw':    v = t; break;
        case 'square': v = t < 0.5 ? 1 : 0; break;
        case 'noise':  v = ctx.rand(Math.floor(st.phase * 64), 7717); break;
        default:       v = 0.5 + 0.5 * Math.sin(t * Math.PI * 2); break;
      }
      return { out: p.bipolar ? v * 2 - 1 : v };
    },
  },

  'g.env': {
    type: 'g.env', title: 'Inviluppo', group: 'Generatori', accent: ACC.gen,
    hint: 'Attacco e decadimento innescati da un impulso.',
    inputs: [{ id: 'trig', kind: 'trigger', label: 'trig' }],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { attack: 5, decay: 320, shape: 1.6 },
    schema: [
      { key: 'attack', label: 'Attacco ms',   min: 0, max: 2000, step: 1, mod: true },
      { key: 'decay',  label: 'Decadim. ms',  min: 1, max: 8000, step: 1, mod: true },
      { key: 'shape',  label: 'Curva',        min: 0.2, max: 5, step: 0.05, mod: true },
    ],
    state: () => ({ v: 0, rising: false }),
    eval: (i, p, ctx, st) => {
      if (i.trig) st.rising = true;
      if (st.rising) {
        st.v += poleCoeff(num(p.attack, 5), ctx.dt) * (1.02 - st.v);
        if (st.v >= 0.999) { st.v = 1; st.rising = false; }
      } else {
        st.v -= poleCoeff(num(p.decay, 320), ctx.dt) * st.v;
      }
      return { out: Math.pow(clamp01(st.v), num(p.shape, 1.6)) };
    },
  },

  'g.const': {
    type: 'g.const', title: 'Costante', group: 'Generatori', accent: ACC.gen,
    hint: 'Un valore fisso, utile come base da modulare.',
    inputs: [], outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { value: 0.5 },
    schema: [{ key: 'value', label: 'Valore', min: -10, max: 10, step: 0.01, mod: true }],
    eval: (_i, p) => ({ out: num(p.value, 0.5) }),
  },

  'g.random': {
    type: 'g.random', title: 'Random', group: 'Generatori', accent: ACC.gen,
    hint: 'Nuovo valore a ogni impulso. Seminato dal numero di frame: identico a ogni export.',
    inputs: [{ id: 'trig', kind: 'trigger', label: 'trig' }],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { min: 0, max: 1 },
    schema: [
      { key: 'min', label: 'Min', min: -10, max: 10, step: 0.01, mod: true },
      { key: 'max', label: 'Max', min: -10, max: 10, step: 0.01, mod: true },
    ],
    state: () => ({ v: 0.5 }),
    eval: (i, p, ctx, st) => {
      if (i.trig) st.v = ctx.rand(ctx.frame, 4241);
      return { out: num(p.min) + (num(p.max, 1) - num(p.min)) * st.v };
    },
  },

  // ------------------------------------------------------------------ math
  'm.op': {
    type: 'm.op', title: 'Operazione', group: 'Matematica', accent: ACC.math,
    hint: 'Due ingressi, un operatore.',
    inputs: [
      { id: 'a', kind: 'scalar', label: 'a' },
      { id: 'b', kind: 'scalar', label: 'b' },
    ],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { op: 'mul' },
    schema: [{ key: 'op', label: 'Operatore', type: 'select', options: ['add', 'sub', 'mul', 'div', 'min', 'max', 'pow'] }],
    eval: (i, p) => {
      const a = num(i.a), b = num(i.b);
      switch (p.op) {
        case 'add': return { out: a + b };
        case 'sub': return { out: a - b };
        case 'div': return { out: b === 0 ? 0 : a / b };
        case 'min': return { out: Math.min(a, b) };
        case 'max': return { out: Math.max(a, b) };
        case 'pow': return { out: Math.pow(Math.abs(a), b) * Math.sign(a || 1) };
        default:    return { out: a * b };
      }
    },
  },

  'm.mix': {
    type: 'm.mix', title: 'Mix', group: 'Matematica', accent: ACC.math,
    hint: 'Interpola fra due segnali.',
    inputs: [
      { id: 'a', kind: 'scalar', label: 'a' },
      { id: 'b', kind: 'scalar', label: 'b' },
      { id: 't', kind: 'scalar', label: 't' },
    ],
    outputs: [{ id: 'out', kind: 'scalar', label: 'out' }],
    params: { amount: 0.5 },
    schema: [{ key: 'amount', label: 'Mix', min: 0, max: 1, step: 0.005, mod: true }],
    eval: (i, p) => {
      const t = i.t !== undefined && i.t !== null && i.t !== 0 ? clamp01(num(i.t)) : clamp01(num(p.amount, 0.5));
      return { out: num(i.a) * (1 - t) + num(i.b) * t };
    },
  },

  // ----------------------------------------------------------------- array
  'arr.pick': {
    type: 'arr.pick', title: 'Preleva', group: 'Array', accent: ACC.arr,
    hint: 'Un elemento da un array.',
    inputs: [{ id: 'in', kind: 'array', label: 'array' }],
    outputs: [{ id: 'out', kind: 'scalar', label: 'valore' }],
    params: { index: 0, gain: 12 },
    schema: [
      { key: 'index', label: 'Indice', min: 0, max: 511, step: 1, mod: true },
      { key: 'gain',  label: 'Guadagno', min: 0, max: 60, step: 0.5, mod: true },
    ],
    eval: (i, p) => {
      const a = i.in;
      if (!a || !a.length) return { out: 0 };
      const k = Math.max(0, Math.min(a.length - 1, Math.round(num(p.index))));
      return { out: a[k] * num(p.gain, 12) };
    },
  },

  'arr.range': {
    type: 'arr.range', title: 'Intervallo', group: 'Array', accent: ACC.arr,
    hint: 'Media, massimo o somma su una porzione di array.',
    inputs: [{ id: 'in', kind: 'array', label: 'array' }],
    outputs: [{ id: 'out', kind: 'scalar', label: 'valore' }],
    params: { from: 0, to: 0.25, mode: 'avg', gain: 12 },
    schema: [
      { key: 'from', label: 'Da',  min: 0, max: 1, step: 0.005, mod: true },
      { key: 'to',   label: 'A',   min: 0, max: 1, step: 0.005, mod: true },
      { key: 'mode', label: 'Modo', type: 'select', options: ['avg', 'max', 'sum'] },
      { key: 'gain', label: 'Guadagno', min: 0, max: 60, step: 0.5, mod: true },
    ],
    eval: (i, p) => {
      const a = i.in;
      if (!a || !a.length) return { out: 0 };
      const n = a.length;
      let i0 = Math.floor(clamp01(num(p.from)) * n);
      let i1 = Math.ceil(clamp01(num(p.to, 0.25)) * n);
      if (i1 <= i0) i1 = i0 + 1;
      if (i1 > n) i1 = n;
      let acc = 0, mx = 0;
      for (let k = i0; k < i1; k++) { acc += a[k]; if (a[k] > mx) mx = a[k]; }
      const count = i1 - i0;
      const v = p.mode === 'max' ? mx : p.mode === 'sum' ? acc : acc / count;
      return { out: v * num(p.gain, 12) };
    },
  },
};

export const SIGNAL_TYPES = Object.keys(SIGNAL_NODES);
export const isSignalNode = (type) => Object.prototype.hasOwnProperty.call(SIGNAL_NODES, type);
